import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "../i18n";
import { useUi } from "../lib/ui";
import { smoothScrollToId } from "../lib/scroll";
import { IMAGES } from "../constants/images";
import ContactForm from "../components/ContactForm";

function SectionHead({ eyebrow, title, lead, light = false }) {
  return (
    <div className="section-head">
      <span className={`eyebrow${light ? " eyebrow-light" : ""}`}>{eyebrow}</span>
      <h2 className="section-title">{title}</h2>
      {lead ? <p className="section-lead">{lead}</p> : null}
    </div>
  );
}

export default function Home() {
  const { t, path } = useI18n();
  const { openContact } = useUi();
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(() => {
    const target = location.state && location.state.scrollTo;
    if (!target) return;
    const id = window.setTimeout(() => smoothScrollToId(target), 80);
    return () => window.clearTimeout(id);
  }, [location.state]);

  const stats = t("home.stats.items") || [];
  const whyItems = t("home.why.items") || [];
  const steps = t("home.how.steps") || [];
  const sectors = t("home.sectors.items") || [];
  const sectorImages = [IMAGES.sectorLithium, IMAGES.sectorOil, IMAGES.sectorInfra];
  const transparencyItems = t("home.transparency.items") || [];
  const columns = t("home.data.columns") || [];
  const rows = t("home.data.rows") || [];
  const dataCards = t("home.data.cards") || [];
  const regions = t("home.regions.items") || [];
  const regionImages = [IMAGES.regionPuna, IMAGES.regionNeuquen, IMAGES.regionBuenosAires];
  const faqs = t("home.faq.items") || [];
  const offices = t("footer.offices") || [];

  return (
    <main id="top">
      {/* HERO */}
      <section className="hero" id="inicio">
        <div className="hero-grid-lines" aria-hidden="true" />
        <div className="container hero-inner">
          <div className="hero-copy">
            <span className="eyebrow eyebrow-light">{t("home.hero.eyebrow")}</span>
            <h1 className="hero-title">{t("home.hero.title")}</h1>
            <p className="hero-lead">{t("home.hero.lead")}</p>
            <p className="hero-lead hero-lead-sm">{t("home.hero.lead2")}</p>
            <div className="hero-actions">
              <button type="button" className="btn btn-accent" onClick={openContact} data-testid="hero-cta-primary">
                {t("home.hero.ctaPrimary")}
              </button>
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={() => smoothScrollToId("circuito")}
                data-testid="hero-cta-secondary"
              >
                {t("home.hero.ctaSecondary")}
              </button>
            </div>
            <p className="hero-badge">{t("home.hero.badge")}</p>
          </div>
          <div className="hero-media">
            <img src={IMAGES.hero} alt={t("home.hero.imageAlt")} loading="eager" />
            <div className="hero-media-tag">
              <span>Vaca Muerta</span>
              <span>Neuqu&eacute;n &middot; AR</span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="section section-stats" id="indicadores">
        <div className="container">
          <h2 className="stats-title">{t("home.stats.title")}</h2>
          <div className="stats-grid">
            {stats.map((s) => (
              <div className="stat-card" key={s.label}>
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
                <span className="stat-detail">{s.detail}</span>
              </div>
            ))}
          </div>
          <p className="stats-note">{t("home.stats.note")}</p>
        </div>
      </section>

      {/* WHY */}
      <section className="section" id="fundamentos">
        <div className="container why-layout">
          <div className="why-media">
            <img src={IMAGES.why} alt={t("home.why.imageAlt")} loading="lazy" />
          </div>
          <div className="why-content">
            <SectionHead eyebrow={t("home.why.eyebrow")} title={t("home.why.title")} lead={t("home.why.lead")} />
            <div className="why-list">
              {whyItems.map((item, i) => (
                <div className="why-item" key={item.title}>
                  <span className="why-index">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="why-item-title">{item.title}</h3>
                    <p className="why-item-text">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="section section-alt" id="sectores">
        <div className="container">
          <SectionHead
            eyebrow={t("home.sectors.eyebrow")}
            title={t("home.sectors.title")}
            lead={t("home.sectors.lead")}
          />
          <div className="sector-grid">
            {sectors.map((s, i) => (
              <article className="sector-card" key={s.title}>
                <div className="sector-media">
                  <img src={sectorImages[i]} alt={s.imageAlt} loading="lazy" />
                  <span className="sector-tag">{s.tag}</span>
                </div>
                <div className="sector-body">
                  <h3 className="sector-title">{s.title}</h3>
                  <p className="sector-text">{s.text}</p>
                  <dl className="sector-metric">
                    <dt>{s.metricLabel}</dt>
                    <dd>{s.metricValue}</dd>
                  </dl>
                  <button
                    type="button"
                    className="link-arrow"
                    onClick={openContact}
                    data-testid={`sector-cta-${i}`}
                  >
                    {t("common.requestReport")}
                    <span aria-hidden="true">&rarr;</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section" id="circuito">
        <div className="container">
          <SectionHead eyebrow={t("home.how.eyebrow")} title={t("home.how.title")} lead={t("home.how.lead")} />
          <ol className="steps">
            {steps.map((s) => (
              <li className="step" key={s.num}>
                <span className="step-num">{s.num}</span>
                <h3 className="step-title">{s.title}</h3>
                <p className="step-text">{s.text}</p>
              </li>
            ))}
          </ol>
          <div className="steps-cta">
            <button type="button" className="btn btn-primary" onClick={openContact} data-testid="process-cta">
              {t("home.how.cta")}
            </button>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="section section-dark" id="transparencia">
        <div className="container transparency-layout">
          <div className="transparency-copy">
            <SectionHead eyebrow={t("home.transparency.eyebrow")} title={t("home.transparency.title")} light />
            <p className="transparency-p">{t("home.transparency.p1")}</p>
            <p className="transparency-p">{t("home.transparency.p2")}</p>
            <div className="transparency-media">
              <img src={IMAGES.transparency} alt={t("home.transparency.imageAlt")} loading="lazy" />
            </div>
          </div>
          <ul className="transparency-list">
            {transparencyItems.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DATA PANEL */}
      <section className="section section-alt" id="panel">
        <div className="container">
          <SectionHead eyebrow={t("home.data.eyebrow")} title={t("home.data.title")} lead={t("home.data.lead")} />
          <div className="panel-layout">
            <div className="panel-table-wrap">
              <table className="data-table">
                <caption>{t("home.data.tableCaption")}</caption>
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c} scope="col">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row[0]}>
                      {row.map((cell, ci) => (
                        <td key={`${row[0]}-${ci}`} data-label={columns[ci]}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="panel-side">
              <img className="panel-image" src={IMAGES.data} alt={t("home.data.imageAlt")} loading="lazy" />
              {dataCards.map((c) => (
                <div className="panel-card" key={c.title}>
                  <h3>{c.title}</h3>
                  <p>{c.text}</p>
                </div>
              ))}
              <button type="button" className="btn btn-primary btn-block" onClick={openContact} data-testid="panel-cta">
                {t("home.data.cta")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="section" id="regiones">
        <div className="container">
          <SectionHead
            eyebrow={t("home.regions.eyebrow")}
            title={t("home.regions.title")}
            lead={t("home.regions.lead")}
          />
          <div className="region-grid">
            {regions.map((r, i) => (
              <article className="region-card" key={r.title}>
                <img src={regionImages[i]} alt={r.imageAlt} loading="lazy" />
                <div className="region-body">
                  <h3>{r.title}</h3>
                  <p>{r.text}</p>
                  <span className="region-office">{[offices[2], offices[1], offices[0]][i] ? [offices[2], offices[1], offices[0]][i].address : ""}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-alt" id="preguntas">
        <div className="container faq-layout">
          <div>
            <SectionHead eyebrow={t("home.faq.eyebrow")} title={t("home.faq.title")} />
            <img className="faq-image" src={IMAGES.reports} alt={t("home.data.imageAlt")} loading="lazy" />
          </div>
          <div className="faq-list">
            {faqs.map((f, i) => (
              <div className={`faq-item${openFaq === i ? " is-open" : ""}`} key={f.q}>
                <button
                  type="button"
                  className="faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  data-testid={`faq-${i}`}
                >
                  <span>{f.q}</span>
                  <span className="faq-icon" aria-hidden="true">
                    {openFaq === i ? "−" : "+"}
                  </span>
                </button>
                {openFaq === i && <p className="faq-a">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section section-contact" id="contacto">
        <div className="container contact-layout">
          <div className="contact-copy">
            <SectionHead
              eyebrow={t("home.contact.eyebrow")}
              title={t("home.contact.title")}
              lead={t("home.contact.lead")}
            />
            <div className="contact-details">
              <h3>{t("home.contact.altTitle")}</h3>
              <dl>
                <dt>{t("home.contact.emailLabel")}</dt>
                <dd>
                  <a href="mailto:consultas@recursosarg.com" data-testid="contact-email">
                    consultas@recursosarg.com
                  </a>
                </dd>
                <dt>{t("home.contact.legalEmailLabel")}</dt>
                <dd>
                  <a href="mailto:legal@recursosarg.com" data-testid="contact-legal-email">
                    legal@recursosarg.com
                  </a>
                </dd>
                <dt>{t("home.contact.phoneLabel")}</dt>
                <dd>
                  <a href="tel:+541143182900" data-testid="contact-phone">
                    +54 11 4318-2900
                  </a>
                </dd>
                <dt>{t("home.contact.hoursLabel")}</dt>
                <dd>{t("home.contact.hoursValue")}</dd>
                <dt>{t("home.contact.officeLabel")}</dt>
                <dd>{offices[0] ? offices[0].address : ""}</dd>
              </dl>
              <Link to={path("/privacy")} className="link-arrow" data-testid="contact-privacy-link">
                {t("nav.privacy")}
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
          <div className="contact-form-wrap">
            <span className="eyebrow">{t("form.subtitle")}</span>
            <h3 className="contact-form-title">{t("form.title")}</h3>
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}
